<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  show: { type: Boolean, required: true },
  src: { type: String, default: new URL('@/assets/lottie/loading.json', import.meta.url).href },
  message: { type: String, default: '' },
  fullscreen: { type: Boolean, default: false },
  blur: { type: Boolean, default: true },
  dim: { type: Boolean, default: true },
  size: { type: String, default: 'md', validator: v => ['sm','md','lg'].includes(v) },
  loop: { type: Boolean, default: true },
  autoplay: { type: Boolean, default: true },
  speed: { type: Number, default: 1 },
  minDuration: { type: Number, default: 400 },
})

/* Tell Vue that <lottie-player> is a valid custom element */
defineOptions({
  compilerOptions: { isCustomElement: tag => tag === 'lottie-player' }
})

const boxSize = (s) => s === 'sm' ? 'w-28 h-28' : s === 'lg' ? 'w-60 h-60' : 'w-40 h-40'

const visible = ref(props.show)
let shownAt = 0
let hideTimer = null

watch(() => props.show, (val) => {
  if (val) {
    clearTimeout(hideTimer)
    shownAt = Date.now()
    visible.value = true
  } else {
    const elapsed = Date.now() - shownAt
    const remaining = props.minDuration - elapsed
    clearTimeout(hideTimer)
    if (remaining > 0) {
      hideTimer = setTimeout(() => { visible.value = false }, remaining)
    } else {
      visible.value = false
    }
  }
}, { flush: 'sync' })
</script>

<template>
  <transition name="fade" appear>
    <div
      v-if="visible"
      :class="[
        fullscreen ? 'fixed inset-0' : 'absolute inset-0',
        'z-50 flex items-center justify-center',
        dim && 'bg-base-100/60',
        blur && 'backdrop-blur-md'
      ]"
      role="alert"
      aria-live="polite"
      aria-busy="true"
    >
      <div class="flex flex-col items-center gap-3 pointer-events-none">
        <div :class="['p-4', boxSize(size)]">
          <lottie-player
            :src="src"
            :speed="speed"
            :autoplay="autoplay"
            :loop="loop"
            mode="normal"
            style="width:100%;height:100%"
          />
        </div>
        <p v-if="message" class="text-xs text-primary">{{ message }}</p>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity .15s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>